import React, { useState, useEffect } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Button } from "@/components/ui/button";
import { Loader2, Info, CreditCard, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";

export default function PayPalCheckout({ itemId, itemName, orderMeta = {}, onSuccess, onClose }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cardBusy, setCardBusy] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/config/paypal"), api.get("/config/payments")])
      .then(([pp, pay]) => setConfig({ ...pp.data, stripe: pay.data.stripe }))
      .catch(() => setConfig({ enabled: false, stripe: false }))
      .finally(() => setLoading(false));
  }, []);

  const payWithCard = async () => {
    setCardBusy(true);
    try {
      const res = await api.post("/stripe/checkout", { item_id: itemId, ...orderMeta, origin_url: window.location.origin });
      window.location.assign(res.data.url);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // A gateway error returns HTML, not JSON — never surface that raw text to a customer.
      const friendly = typeof detail === "string" && detail.length < 200 && !detail.includes("<")
        ? detail
        : "Card payment could not be started just now. Please try again in a moment, or contact us and we'll process your order directly.";
      toast.error(friendly);
      setCardBusy(false);
    }
  };

  if (loading) return <div className="py-10 text-center" data-testid="paypal-loading"><Loader2 className="w-6 h-6 animate-spin mx-auto text-teal-600" /></div>;

  if (!config?.enabled && !config?.stripe) {
    return (
      <div className="py-4">
        <div className="flex gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
          <Info className="w-5 h-5 shrink-0" />
          <p>Online payments are being activated. In the meantime, contact us and we'll process your order for <strong>{itemName}</strong> directly.</p>
        </div>
        <Button asChild className="w-full mt-4 bg-teal-600 hover:bg-teal-700" data-testid="checkout-enquire">
          <Link to="/contact" onClick={onClose}>Contact us to purchase</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="py-2 space-y-4" data-testid="checkout-options">
      {config.stripe && (
        <Button onClick={payWithCard} disabled={cardBusy} className="w-full h-12 rounded-full bg-slate-900 hover:bg-teal-700 text-base" data-testid="stripe-pay-btn">
          {cardBusy ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />} Pay with card
        </Button>
      )}
      {config.stripe && config.enabled && (
        <div className="flex items-center gap-3 text-xs text-slate-400"><span className="flex-1 h-px bg-slate-200" />or<span className="flex-1 h-px bg-slate-200" /></div>
      )}
      {config.enabled && (
        <div data-testid="paypal-buttons">
          <PayPalScriptProvider options={{ clientId: config.client_id, currency: config.currency }}>
            <PayPalButtons
              style={{ layout: "vertical", color: "gold", shape: "pill" }}
              createOrder={async () => {
                try {
                  const res = await api.post("/orders", { item_id: itemId, ...orderMeta });
                  return res.data.id;
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Could not start checkout.");
                  throw e;
                }
              }}
              onApprove={async (data) => {
                const res = await api.post(`/orders/${data.orderID}/capture`);
                toast.success("Payment successful!");
                onSuccess?.(res.data);
              }}
              onError={() => toast.error("Payment could not be completed. Please try again.")}
            />
          </PayPalScriptProvider>
        </div>
      )}
      <p className="flex items-center justify-center gap-1.5 text-[11px] text-slate-400"><Lock className="w-3 h-3" /> Secure checkout · Card payments by Stripe{config.enabled ? " · PayPal" : ""}</p>
    </div>
  );
}
